import notifIcon from "../assets/images/screens/notification_settings.webp";
import guruVisionScreenShot from "../assets/images/goalguru_prematch_predictions.webp";
import pastMatchesScreenShot from "../assets/images/screens/past_comparison.webp";
import smartAlertScreenShot from "../assets/images/goalguru_smart_alert_editor.webp";
import matchStatsScreenShot from "../assets/images/goalguru_live_match_stats.webp";
import aiChatScreenShot from "../assets/images/goalguru_ask_guru.webp";
import leagueStatsScreenShot from "../assets/images/ss_league_stats.webp";
import liveMatchesScreenShot from "../assets/images/goalguru_live_matches_list.webp";
import backtestOverviewScreenShot from "../assets/images/goalguru_backtest_verdict.webp";
import preMatchAlertScreenShot from "../assets/images/goalguru_prematch_alert.webp";
import livePredictionsScreenShot from "../assets/images/screens/live_predictions.webp";
import guruSummaryScreenShot from "../assets/images/screens/guru_today.webp";
import trackRecordScreenShot from "../assets/images/screens/track_record.webp";
import lineupsScreenShot from "../assets/images/goalguru_lineups_pitch.webp";

export interface FeatureData {
  id: string;
  title: string;
  description: string;
  image: string;
  imageAlt: string;
  // The image's pixel size, for the <img> width/height attributes (no layout shift).
  imageWidth?: number;
  imageHeight?: number;
  link: string;
  imagePosition: "left" | "right";
  // Selects this feature for the homepage's condensed highlight grid.
  highlight?: boolean;
  // One-sentence blurb used by the homepage highlight grid instead of the
  // full `description` (which stays in use on the all-features page and the
  // feature's own detail page).
  shortDescription?: string;
}

// Order intentionally leads with the Smart Alert loop + AI features (the real
// differentiator) and pushes generic stats features down. The visible 01/02/…
// numbers in FeatureItem are derived from array index, so they follow this
// order automatically. imagePosition alternates right/left by position to keep
// the zigzag layout intact after reordering.
export const featuresData: FeatureData[] = [
  {
    id: "smart-alerts",
    title: "Smart Alerts & Personalized Tracking",
    description: "Tired of checking multiple apps to track specific match conditions? With Goal Guru, you can create your own personalized alerts based on live match conditions — from raw events like goals and shots to calculated stats like xG, possession, goal conversion, and goalkeeper save rate. Want to be notified when there are more than 2 goals in the first half? Or when a team's xG passes 1.5 without scoring? You can set it all up! Compare the two teams head to head with share conditions — the home team has at least 80% of both teams' dangerous attacks — or aim an alert at a single match you pick with a match alert.",
    shortDescription: "Create custom alerts on any live condition — goals, xG, possession, cards — and get notified the instant it happens.",
    image: smartAlertScreenShot,

    imageWidth: 414,

    imageHeight: 900,
    imageAlt: "Smart alert editor with live match conditions in Goal Guru",
    link: "/smart-football-notifications/",
    imagePosition: "right",
    highlight: true,
  },
  {
    id: "pre-match-alerts",
    title: "Pre-Match Alerts: Know Which Matches Fit Before Kickoff",
    description: "Pre-match alerts check every upcoming fixture against your conditions — Goal Guru's own prediction, each team's form over the last 3, 5, or 10 matches, the head-to-head record, and bookmaker odds — and tell you which matches fit. Get a daily summary at the time you choose, or a reminder 30 minutes to 3 hours before kickoff. Start from one of 12 templates, and see which matches fit right now before you save.",
    shortDescription: "Hear about the matches that fit your conditions before they kick off — by prediction, form, head to head, or odds.",
    image: preMatchAlertScreenShot,

    imageWidth: 414,

    imageHeight: 900,
    imageAlt: "Pre-match alert listing the upcoming matches that fit its conditions",
    link: "/features/pre-match-alerts/",
    imagePosition: "left",
    highlight: true,
  },
  {
    id: "backtesting",
    title: "Backtest Your Smart Alerts",
    description: "Before going live, run your Smart Alert setup through historical pattern analysis. Configure your conditions — goals, corners, cards, timing windows — then see exactly how often your alert would have triggered across past games, along with the trigger rate and match context. Fine-tune your setup with confidence before it goes live.",
    shortDescription: "Test your Smart Alert conditions against real historical matches before trusting them live.",
    image: backtestOverviewScreenShot,

    imageWidth: 414,

    imageHeight: 900,
    imageAlt: "Backtest report with its verdict and at-a-glance results",
    link: "/blog/backtesting-smart-alerts/",
    imagePosition: "right",
    highlight: true,
  },
  {
    id: "custom-notifications",
    title: "Custom Notifications & Favorites",
    description: "Stay updated with real-time push notifications when your Smart Alert criteria are met. Whether you're waiting for a goal, a match turning point, or any other event. We’ll let you know instantly. You can fully customize your notification settings. Every notification says which kind of alert sent it, and one alerts inbox holds them all, with a filter by kind.",
    image: notifIcon,

    imageWidth: 540,

    imageHeight: 1174,
    imageAlt: "Notification Icon",
    link: "/features/custom-notifications/",
    imagePosition: "left",
  },
  {
    id: "guru-vision",
    title: "Guru Vision: AI Football Predictions & Match Analysis",
    description: "Get AI football predictions and analysis from Guru Vision, our advanced football model. Pre-Match Predictions cover result confidence, projected scorelines, goals, both teams to score, and corners before kickoff — then Live Guru Vision keeps analysing the game as it unfolds, regenerating on demand with the latest live stats, recent form, and head-to-head history. Dive deeper with Ask Guru, our AI agent, to discuss any aspect of the match. Guru Vision is organised into four tabs — Today, Live, Surprises, and Track record — and Striker and Guru predictions now add a score table with the most likely results and ranges for total goals and corners, while live predictions show who is likely to score next.",
    shortDescription: "AI match predictions and analysis, before kickoff and live, from Guru Vision.",
    image: guruVisionScreenShot,

    imageWidth: 414,

    imageHeight: 900,
    imageAlt: "Guru Vision pre-match predictions for a match in Goal Guru",
    link: "/features/guru-vision/",
    imagePosition: "right",
    highlight: true,
  },
  {
    id: "live-predictions",
    title: "Live In-Play Predictions, Updated Every Minute",
    description: "Watch the win probability move as the match does. Live Predictions refreshes roughly every minute during play — home/draw/away probabilities, a next-15-minutes goal probability, rest-of-half and rest-of-match projections, projected goals and a goal ladder, the next goal, plus BTTS and final goal and corner ranges. Currently free for every signed-in user, on every plan. Guru Vision's Live tab lists every match in play with its pre-match call, so you can see at a glance where the game is going to plan and where it isn't.",
    shortDescription: "Win probability that updates every minute while the match is live — free for every signed-in user right now.",
    image: livePredictionsScreenShot,

    imageWidth: 540,

    imageHeight: 1174,
    imageAlt: "Live in-play predictions updating during a match",
    link: "/features/live-predictions/",
    imagePosition: "left",
    highlight: true,
  },
  {
    id: "track-record",
    title: "Track Record: How Accurate Are the Predictions?",
    description: "Every pre-match prediction is graded the morning after its match. The Track record tab shows how often the model was right over a day, 7, 30, or 90 days, or all time — overall, for its most confident picks, and for each market — plus how often each chance the model gave actually came true. A real, daily-updated record instead of a marketing number.",
    shortDescription: "How often the predictions are right — graded daily, by market and by confidence.",
    image: trackRecordScreenShot,

    imageWidth: 540,

    imageHeight: 1174,
    imageAlt: "Goal Guru app icon",
    link: "/features/track-record/",
    imagePosition: "right",
    highlight: true,
  },
  {
    id: "guru-summary",
    title: "Guru Summary: Your Daily Predictions Digest",
    description: "One screen for every match worth knowing about today, in Guru Vision's Today tab: Best Predictions, Safe Matches, Strong Value and Value Matches, Starting Soon, and Today's Results — the model's pre-match call next to what actually happened. Live matches and live Surprises have tabs of their own.",
    shortDescription: "The day's best picks, clearest calls, and value picks in one digest.",
    image: guruSummaryScreenShot,

    imageWidth: 540,

    imageHeight: 1174,
    imageAlt: "Guru Summary digest of today's best predictions",
    link: "/features/guru-summary/",
    imagePosition: "left",
  },
  {
    id: "past-matches",
    title: "Past Matches Stats & Head-to-Head Analysis",
    description: "Go beyond the win-draw-loss record. Goal Guru's Past Matches section gives you deep head-to-head statistics including goal distributions by 15-minute period, comeback rates, BTTS and clean sheet percentages, shot efficiency metrics, and attacking averages split by first and second half. Filter by home or away context to get the most relevant picture for any upcoming fixture, and switch between each team's last 3, 5, or 10 matches.",
    shortDescription: "Head-to-head history, goal timing, and comeback rates for every fixture.",
    image: pastMatchesScreenShot,

    imageWidth: 540,

    imageHeight: 1174,
    imageAlt: "Past Matches Head-to-Head Stats",
    link: "/features/past-matches/",
    imagePosition: "right",
  },
  {
    id: "live-stats",
    title: "Live Football Stats & Events",
    description: "Track football matches from all over the world with real-time updates. Goal Guru provides detailed statistics like goals, corners, yellow/red cards, shots on/off target, and more.",
    image: liveMatchesScreenShot,

    imageWidth: 414,

    imageHeight: 900,
    imageAlt: "Live football matches with scores and match minutes",
    link: "/live-football-alerts/",
    imagePosition: "left",
  },
  {
    id: "match-details",
    title: "In-Depth Match Details Screen",
    description: "Tap into any live match and access a comprehensive match overview screen. View the full breakdown of events as they happen — including minute-by-minute updates, goals, cards, corners, and even team momentum shifts — plus the lineups, a shot map, predictions for the match, and the alerts that fired for it.",
    image: matchStatsScreenShot,

    imageWidth: 414,

    imageHeight: 900,
    imageAlt: "Live match stats with the momentum timeline",
    link: "/features/match-details/",
    imagePosition: "right",
  },
  {
    id: "lineups-shot-map",
    title: "Lineups, Shot Map & xGOT",
    description: "See both starting XIs on a pitch in their formation, with goals, cards, and substitutions on each player. The shot map plots every shot with its xG — and, for shots on target, its xGOT — and the xG timeline shows how each team's chances built up over the match.",
    image: lineupsScreenShot,

    imageWidth: 414,

    imageHeight: 900,
    imageAlt: "Starting lineups on a pitch with cards and substitutions",
    link: "/features/lineups-shot-map/",
    imagePosition: "left",
  },
  {
    id: "league-stats",
    title: "Check League Stats & Standings",
    description: "Dive into detailed league statistics and standings for all major football leagues worldwide. Goal Guru provides up-to-date league tables, team performance metrics, and player statistics to help you stay informed about your favorite leagues.",
    image: leagueStatsScreenShot,

    imageWidth: 416,

    imageHeight: 900,
    imageAlt: "League Stats Screenshot",
    link: "/features/league-stats/",
    imagePosition: "right",
  },
  {
    id: "ai-assistant",
    title: "Ask Guru AI to Help You",
    description: "Need quick insights or stats about how to create Smart Alerts? Just ask Goal Guru's AI assistant, Ask Guru. Whether you want to know the best conditions for a specific league, need help setting up Smart Alerts, or want to understand football stats better, Ask Guru is here to assist you 24/7.",
    image: aiChatScreenShot,

    imageWidth: 414,

    imageHeight: 900,
    imageAlt: "Ask Guru AI chat answering a football question",
    link: "/features/ai-assistant/",
    imagePosition: "left",
  },
];
